import React, { useEffect, useState } from "react";
import { Button, Grid, Typography, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import CircuitTokenCard from "../../../components/other/CircuitTokenCard";
import {
  BCPArrowUpArrowDown,
  BCPCalendarDay,
  BCPFilter,
  BCPXmark,
} from "../../../components/icons/bcp/BCPIcons";
import OptionsMenu from "../../../components/layout/OptionsMenu";
import {
  clearFilterSx,
  getFilterSx,
  updateFilters,
} from "../../../components/utilities/SortFilterFunctions";
import ListResponsive from "../../../components/other/ListResponsive";
import InfoContainer from "../../../components/layout/InfoContainer";

const filterAndSortCircuitTokens = (tokens, filters) => {
  if (tokens?.length && filters.length) {
    let tempTokens = [...tokens];

    const sortFilters = [];
    const circuitFilters = [];
    const typeFilters = [];
    const statusFilters = [];

    filters.forEach((filter) => {
      if (filter?.includes("circuit-")) {
        const circuitName = filter.split("-").slice(1).join("-");
        circuitFilters.push(circuitName);
      } else if (filter?.includes("type-")) {
        const typeName = filter.split("-").slice(1).join("-");
        typeFilters.push(typeName);
      } else if (filter?.includes("status-")) {
        const statusName = filter.split("-")[1];
        statusFilters.push(statusName);
      } else if (filter?.includes("sort-")) {
        sortFilters.push(filter);
      }
    });

    if (circuitFilters?.length) {
      tempTokens = tempTokens.filter((token) =>
        circuitFilters.includes(token.league?.name)
      );
    }

    if (typeFilters?.length) {
      tempTokens = tempTokens.filter((token) =>
        typeFilters.includes(token.tokenType)
      );
    }

    if (statusFilters?.length) {
      tempTokens = tempTokens.filter((token) =>
        statusFilters.includes(token.status)
      );
    }

    if (sortFilters?.length && tempTokens?.length) {
      sortFilters.forEach((filter) => {
        if (filter === "sort-date-asc") {
          tempTokens = tempTokens.sort((a, b) => {
            const dateA = moment(a.created_at);
            const dateB = moment(b.created_at);
            return dateA.diff(dateB);
          });
        } else if (filter === "sort-date-desc") {
          tempTokens = tempTokens.sort((a, b) => {
            const dateA = moment(a.created_at);
            const dateB = moment(b.created_at);
            return dateB.diff(dateA);
          });
        }
      });
    }

    return tempTokens;
  } else if (!filters.length) {
    return null;
  }
  return [];
};

export default function OrgDashCircuitTokens({ tokens, loading, eventsData }) {
  const navigate = useNavigate();
  const theme = useTheme();

  const [filteredTokens, setFilteredTokens] = useState(null);
  const [filters, setFilters] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);

  const filterOptions = {
    circuits: tokens
      ? [...new Set(tokens.map((token) => token.league?.name).filter(Boolean))]
          .map((circuit) => ({
            name: circuit,
            count: tokens.filter((token) => token.league?.name === circuit)
              .length,
          }))
          .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
      : [],
    types: tokens
      ? [
          ...new Set(tokens.map((token) => token.tokenType).filter(Boolean)),
        ].sort()
      : [],
    statuses: tokens
      ? [...new Set(tokens.map((token) => token.status).filter(Boolean))].sort()
      : [],
  };

  useEffect(() => {
    if (tokens?.length && filters.length) {
      const tempTokens = filterAndSortCircuitTokens(tokens, filters);
      setFilteredTokens(tempTokens);
    } else if (!filters.length) {
      setFilteredTokens(null);
    }
  }, [filters, tokens]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={8}>
        <Typography variant="h3" fontWeight="700">
          Circuit Tokens
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <Button
          variant="contained"
          onClick={() => navigate("/organize/tokens")}
          startIcon={<BCPCalendarDay />}
          fullWidth
        >
          View Circuit Tokens
        </Button>
      </Grid>

      <Grid item xs={12}>
        {!loading && !tokens?.length ? (
          <InfoContainer
            title="No circuit tokens found"
            body={
              "You don't have any circuit tokens yet. Tokens will appear here when you receive them from circuits."
            }
            sx={{ mt: 1 }}
          />
        ) : (
          <ListResponsive
            name="Circuit Tokens"
            originalList={
              loading
                ? null
                : filteredTokens
                  ? filteredTokens
                  : tokens?.length
                    ? tokens
                    : []
            }
            doPages
            initialRowsPerPage={3}
            itemsPerPage={[3]}
            centerNav
            actionButton={[
              {
                label: "Sort",
                startIcon: <BCPArrowUpArrowDown />,
                onClick: (e) => {
                  setAnchorEl(e.currentTarget);
                },
                disabled: loading || !tokens?.length,
                sx: {
                  ...getFilterSx(
                    Boolean(
                      filters.length &&
                        filters.some((item) => item.includes("sort"))
                    ),
                    filters,
                    theme
                  ),
                  mr: 1,
                },
              },
              {
                label: "Filters",
                startIcon: <BCPFilter />,
                onClick: (e) => {
                  setAnchorEl2(e.currentTarget);
                },
                disabled: loading || !tokens?.length,
                sx: {
                  ...getFilterSx(
                    Boolean(
                      filters.length &&
                        filters.some(
                          (item) =>
                            item.includes("filter") ||
                            item.includes("circuit-") ||
                            item.includes("type-") ||
                            item.includes("status-")
                        )
                    ),
                    filters,
                    theme
                  ),
                  ml: 1,
                  mr: 1,
                },
              },
              {
                label: "Clear Filters",
                startIcon: <BCPXmark />,
                disabled: loading || !tokens?.length,
                sx: {
                  ...clearFilterSx(false, filters, theme),
                  ml: 1,
                },
                onClick: () => {
                  updateFilters(null, null, null, setFilters);
                  setFilteredTokens(null);
                  setAnchorEl(null);
                  setAnchorEl2(null);
                },
              },
            ]}
            buttonGroup={false}
            rowProps={{
              mapFunction: (row, index) => {
                const eventDetail = eventsData?.find(
                  (event) => event.id === row.eventId
                );
                return (
                  <Grid key={index} item xs={12} sm={6} md={4} sx={{ p: 1 }}>
                    <CircuitTokenCard token={row} eventDetail={eventDetail} />
                  </Grid>
                );
              },
            }}
          />
        )}
      </Grid>

      <OptionsMenu
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        items={[
          {
            name: "sort-date-desc",
            label: "Date Created (Newest First)",
            selected: filters.includes("sort-date-desc"),
            onClick: () =>
              updateFilters("sort-date-desc", false, filters, setFilters),
          },
          {
            name: "sort-date-asc",
            label: "Date Created (Oldest First)",
            selected: filters.includes("sort-date-asc"),
            onClick: () =>
              updateFilters("sort-date-asc", false, filters, setFilters),
          },
        ]}
        name="tokens-sort-menu"
      />

      <OptionsMenu
        anchorEl={anchorEl2}
        setAnchorEl={setAnchorEl2}
        items={[
          ...filterOptions.statuses.map((status) => ({
            name: `status-${status}`,
            label: `${status.charAt(0).toUpperCase() + status.slice(1)} Tokens`,
            selected: filters.includes(`status-${status}`),
            onClick: () =>
              updateFilters(
                `status-${status}`,
                filterOptions.statuses
                  .filter((s) => s !== status)
                  .map((s) => `status-${s}`),
                filters,
                setFilters
              ),
          })),
          ...(filterOptions.statuses.length > 0 ? [{ divider: true }] : []),

          ...filterOptions.circuits.map((circuit) => ({
            name: `circuit-${circuit.name}`,
            label: `${circuit.name} (${circuit.count})`,
            selected: filters.includes(`circuit-${circuit.name}`),
            onClick: () =>
              updateFilters(
                `circuit-${circuit.name}`,
                false,
                filters,
                setFilters
              ),
          })),
          ...(filterOptions.circuits.length > 0 ? [{ divider: true }] : []),

          ...filterOptions.types.map((type) => ({
            name: `type-${type}`,
            label: `${type} Type`,
            selected: filters.includes(`type-${type}`),
            onClick: () =>
              updateFilters(
                `type-${type}`,
                filterOptions.types
                  .filter((t) => t !== type)
                  .map((t) => `type-${t}`),
                filters,
                setFilters
              ),
          })),
        ]}
        name="tokens-filter-menu"
      />
    </Grid>
  );
}
